"use client";

import { useState } from "react";
import { Frame, Slider, Verdict, Note } from "./parts";

const L = {
    fr: {
        title: "La mémoire d'une liste face à celle d'un générateur",
        count: "Nombre de valeurs produites",
        list: "Liste",
        gen: "Générateur",
        listWins: "En dessous d'une poignée de valeurs, le générateur coûte plus cher que la liste : son objet pèse plus lourd que les quelques entiers qu'il remplace. Le classement s'inverse vraiment, il ne fait pas que se resserrer.",
        genWins: "Le générateur reste à son poids fixe pendant que la liste grandit avec les données. C'est là qu'il devient le seul praticable, sur un fichier qu'on ne peut pas charger en entier.",
        ratio: "soit",
        times: "fois plus",
        note: "Les ordres de grandeur sont ceux de CPython sur une machine 64 bits : environ 28 octets par entier plus 8 octets de pointeur dans la liste, et environ 200 octets pour l'objet générateur, quelle que soit la suite qu'il produit. Mesurez les vôtres avec sys.getsizeof.",
    },
    en: {
        title: "The memory of a list against that of a generator",
        count: "Number of values produced",
        list: "List",
        gen: "Generator",
        listWins: "Below a handful of values the generator costs more than the list: its object weighs more than the few integers it replaces. The ranking genuinely reverses, it does not merely narrow.",
        genWins: "The generator stays at its fixed weight while the list grows with the data. That is where it becomes the only workable option, on a file too large to load at once.",
        ratio: "that is",
        times: "times more",
        note: "The orders of magnitude are those of CPython on a 64-bit machine: about 28 bytes per integer plus an 8-byte pointer inside the list, and about 200 bytes for the generator object, whatever sequence it produces. Measure your own with sys.getsizeof.",
    },
};

// CPython 64 bits : un petit entier pèse 28 octets, plus 8 octets de pointeur
// dans le tableau de la liste. L'objet générateur est constant, autour de 200.
const PAR_VALEUR = 36;
const GENERATEUR = 200;

const poids = (o, lang) => {
    const loc = lang === "fr" ? "fr-FR" : "en-US";
    if (o < 1024) return `${Math.round(o)} o`;
    if (o < 1024 * 1024) return `${(o / 1024).toLocaleString(loc, { maximumFractionDigits: 1 })} Ko`;
    return `${(o / 1024 / 1024).toLocaleString(loc, { maximumFractionDigits: 1 })} Mo`;
};

export default function GeneratorMemory({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [exposant, setExposant] = useState(3);

    const n = Math.round(10 ** exposant);
    const memListe = n * PAR_VALEUR;
    const rapport = memListe / GENERATEUR;
    const listeGagne = memListe < GENERATEUR;

    const barre = (valeur, max, couleur) => (
        <div className="h-3 rounded-full bg-gray-100 dark:bg-white/[0.06] overflow-hidden">
            <div
                className={`h-full rounded-full transition-all duration-300 ${couleur}`}
                style={{ width: `${Math.max(2, (Math.log10(valeur) / Math.log10(max)) * 100)}%` }}
            />
        </div>
    );

    const max = 10 ** 7 * PAR_VALEUR;

    return (
        <Frame icon="database-linear" title={t.title}>
            <Slider
                label={t.count}
                value={exposant}
                onChange={setExposant}
                min={0}
                max={7}
                step={1}
                display={n.toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}
            />

            <div className="mt-6 space-y-4">
                <div>
                    <div className="flex justify-between items-baseline mb-1.5">
                        <span className="text-sm text-gray-600 dark:text-lightBlue">{t.list}</span>
                        <span className="text-sm font-bold tabular-nums dark:text-white">
                            {poids(memListe, lang)}
                        </span>
                    </div>
                    {barre(memListe, max, "bg-gray-400 dark:bg-grayDark-500")}
                </div>
                <div>
                    <div className="flex justify-between items-baseline mb-1.5">
                        <span className="text-sm text-gray-600 dark:text-lightBlue">{t.gen}</span>
                        <span className="text-sm font-bold tabular-nums dark:text-white">
                            {poids(GENERATEUR, lang)}
                        </span>
                    </div>
                    {barre(GENERATEUR, max, "bg-primary")}
                </div>
            </div>

            <div className="mt-4 text-sm text-gray-500 dark:text-lightBlue/80">
                {t.ratio}{" "}
                <span className="font-bold dark:text-white">
                    {rapport >= 1
                        ? `${Math.round(rapport).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} ${t.times}`
                        : `${(1 / rapport).toFixed(1)} ${t.times}`}
                </span>{" "}
                {rapport >= 1 ? (lang === "fr" ? "pour la liste" : "for the list") : (lang === "fr" ? "pour le générateur" : "for the generator")}
            </div>

            <Verdict tone={listeGagne ? "warn" : "good"}>{listeGagne ? t.listWins : t.genWins}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
